// src/data/dataProcessing.js
//
// Data layer for FPL Wrapped.
//
// The backend pre-computes all analysis and returns a JSON blob that already
// matches the shape components expect — there is no client-side sheet parsing
// or heavy reshaping here.  This file is intentionally thin.
//
// Excel / XLSX parsing has been removed.  Use git history to recover it.

import { DEFAULT_PRIZE_DATA } from './constants';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const apiUrl = (path) => `${API_BASE_URL}${path}`;

/**
 * Fetch pre-computed league analysis from the API.
 *
 * Returns the blob exactly as the backend produced it — components receive
 * the same shape they previously got from processData(sheets).
 *
 * @param {number} leagueId
 * @returns {Promise<Object>} Processed data object ready for the DataContext
 */
export const fetchLeagueData = async (leagueId) => {
  const id  = encodeURIComponent(leagueId);
  const url = apiUrl(`/api/league/${id}`);

  const controller = new AbortController();
  const timeoutId  = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      let msg = 'Failed to fetch league data';
      try {
        const err = await response.json();
        msg = err.error || msg;
      } catch {
        msg = `${msg}: ${response.statusText}`;
      }
      throw new Error(msg);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw error;
  }
};

/**
 * Trigger a background seed for a league.
 *
 * Called immediately when the user types a valid league ID format.
 * The seed runs server-side; the frontend polls /status to track progress.
 *
 * @param {number}  leagueId
 * @param {string}  clientId  UUID from sessionStorage, used for abort-on-change
 * @returns {Promise<{status: string, league_id: number}>}
 */
export const triggerSeed = async (leagueId, clientId) => {
  const response = await fetch(apiUrl(`/api/league/${encodeURIComponent(leagueId)}/seed`), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ client_id: clientId }),
  });

  if (!response.ok && response.status !== 429) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to start seed');
  }

  return response.json();
};

/**
 * Abort an in-progress seed (called when user changes league ID input).
 *
 * @param {number} leagueId
 * @returns {Promise<void>}
 */
export const abortSeed = async (leagueId) => {
  try {
    await fetch(apiUrl(`/api/league/${encodeURIComponent(leagueId)}/seed`), { method: 'DELETE' });
  } catch {
    // Fire-and-forget — backend aborts on its own if request doesn't reach it
  }
};

/**
 * Poll seed/analysis status for a league.
 *
 * @param {number} leagueId
 * @returns {Promise<{
 *   status: string,
 *   league_name: string|null,
 *   total_players: number|null,
 *   managers: Array,
 *   overview_ready: boolean,
 *   progress: number
 * }>}
 */
export const getSeedStatus = async (leagueId) => {
  const response = await fetch(apiUrl(`/api/league/${encodeURIComponent(leagueId)}/status`));
  if (!response.ok) {
    throw new Error('Failed to fetch seed status');
  }
  return response.json();
};

/**
 * Process prize configuration from backend data.
 * Returns DEFAULT_PRIZE_DATA when backend returns empty array (prize config is client-side).
 */
export const processPrizeConfiguration = (data) => {
  if (!data || data.length === 0) return DEFAULT_PRIZE_DATA;

  const modeRow = data.find(row => row.Setting === 'PrizeMode');
  const config  = { ...DEFAULT_PRIZE_DATA, mode: modeRow?.Value || 'whatIf', customPrizes: [] };

  data.forEach(row => {
    if      (row.Setting === 'TotalPool')       config.totalPool   = row.Value ?? 500;
    else if (row.Setting === 'FirstPlacePrize') config.firstPrize  = row.Value;
    else if (row.Setting === 'SecondPlacePrize') config.secondPrize = row.Value;
    else if (row.Setting === 'ThirdPlacePrize') config.thirdPrize  = row.Value;
    else if (row.Setting === 'RankJumperPrize') config.rankJumperPrize = row.Value;
    else if (row.Setting === 'MonthlyWinnerPrize') config.monthlyWinnerPrize = row.Value;
    else if (row.Setting?.startsWith('CustomPrize')) config.customPrizes.push(row);
  });

  return config;
};
